/**
 * Shared handler for rejected Redux Toolkit thunks.
 *
 * Why this exists
 * ---------------
 * `dispatch(thunk).unwrap().finally(...)` does NOT handle a rejection -
 * `.finally()` passes it straight through. Fifteen screens used that shape, so
 * any failing API call during navigation became an *unhandled promise
 * rejection*.
 *
 * On Android that is silent. In a browser webpack-dev-server catches it and
 * throws its overlay up over the app, and because the reason is a plain object
 * rather than an Error it renders as the unhelpful:
 *
 *     Uncaught runtime errors: ERROR [object Object]
 *
 * The reason is usually `{}`: the axios interceptor rejects with
 * `error.response.data`, and Redux Toolkit's `miniSerializeError` keeps only
 * string `name`/`message`/`stack`/`code` properties - so a payload without any
 * of those serialises down to an empty object, losing the message entirely.
 * That is why `err?.message` is often undefined here and a fallback is needed.
 */

import {ZenToast} from 'zen-core-elements';

export const DEFAULT_API_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Best-effort message for anything a rejected thunk can carry: an Error, the
 * API's `{message}` / `{error}` payload, a bare string, or `{}`.
 */
export function toErrorMessage(error: unknown): string {
  if (!error) {
    return DEFAULT_API_ERROR_MESSAGE;
  }
  if (typeof error === 'string') {
    return error || DEFAULT_API_ERROR_MESSAGE;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const candidate = record.message ?? record.error ?? record.details ?? record.msg;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return DEFAULT_API_ERROR_MESSAGE;
}

/**
 * Drop-in `.catch()` handler: surfaces the failure to the operator instead of
 * letting it escape as an unhandled rejection.
 *
 *   dispatch(thunk(...)).unwrap().catch(showApiError).finally(...)
 *
 * Deliberately does not rethrow - these call sites have no recovery path
 * beyond telling the user, and rethrowing would recreate the original problem.
 */
export function showApiError(error: unknown): void {
  // Keep the raw reason in the console for debugging; the toast is for the user.
  console.warn('[API error]', error);
  ZenToast({message: toErrorMessage(error), type: 'error'});
}

export default showApiError;
